import { PRODUCT_BY_ID, type ProductId } from '@/config/products';
import {
  canLaunchProduct,
  getProductEntryRoute,
  getProductLaunchUrl,
  parseTenantProducts,
  type TenantProductsMap,
} from '@/utils/productAccess';
import { storage } from '@/utils/storage';

type NavigateFn = (path: string) => void;

async function fetchProductLaunchToken(productId: ProductId): Promise<string | null> {
  const token = storage.getAccessToken();
  if (!token) return null;

  const base = import.meta.env.VITE_API_URL || '/api/v1';
  const res = await fetch(`${base}/auth/product-launch?product=${productId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const json = await res.json();
  return json?.data?.exchangeToken ?? json?.exchangeToken ?? null;
}

export async function launchProduct(
  productId: ProductId,
  role: string | null | undefined,
  options: {
    navigate: NavigateFn;
    access?: TenantProductsMap;
    settings?: Record<string, unknown> | null;
    preview?: boolean;
  }
): Promise<void> {
  const access = options.access ?? parseTenantProducts(options.settings);
  const action = canLaunchProduct(productId, access, role);

  if (action === 'denied') {
    options.navigate('/workspace');
    return;
  }

  const product = PRODUCT_BY_ID[productId];

  if (product.launchMode === 'internal' || productId === 'pm') {
    options.navigate(getProductEntryRoute(productId, role));
    return;
  }

  if (product.appUrl && (action === 'launch' || action === 'preview')) {
    const exchangeToken =
      action === 'launch' ? await fetchProductLaunchToken(productId) : null;
    const url = getProductLaunchUrl(productId, {
      exchangeToken: exchangeToken ?? undefined,
      preview: action === 'preview' || options.preview,
    });
    if (url) {
      window.location.href = url;
      return;
    }
  }

  if (action === 'preview') {
    options.navigate(`/workspace?product=${productId}`);
    return;
  }

  options.navigate(getProductEntryRoute(productId, role));
}
